/**
 * AZERTY (traditional French, Windows KBDFR): maps a physical code to the unshifted
 * character that key emits. Letters are uppercase by table convention; accented chars
 * stay in their unshifted lowercase form. BracketLeft is a dead circumflex on a real
 * board; here it is a legend only. The ISO key left of W ("<>", IntlBackslash) is outside
 * the US-Qwerty code block; the assembly layer adds it.
 */
export const AZERTY_CODE_TO_CHAR: Record<string, string> = {
  Backquote: "²", // superscript two
  Digit1: "&",
  Digit2: "é", // e acute
  Digit3: '"',
  Digit4: "'",
  Digit5: "(",
  Digit6: "-",
  Digit7: "è", // e grave
  Digit8: "_",
  Digit9: "ç", // c cedilla
  Digit0: "à", // a grave
  Minus: ")",
  Equal: "=",
  KeyQ: "A",
  KeyW: "Z",
  KeyE: "E",
  KeyR: "R",
  KeyT: "T",
  KeyY: "Y",
  KeyU: "U",
  KeyI: "I",
  KeyO: "O",
  KeyP: "P",
  BracketLeft: "^",
  BracketRight: "$",
  Backslash: "*",
  KeyA: "Q",
  KeyS: "S",
  KeyD: "D",
  KeyF: "F",
  KeyG: "G",
  KeyH: "H",
  KeyJ: "J",
  KeyK: "K",
  KeyL: "L",
  Semicolon: "M",
  Quote: "ù", // u grave
  KeyZ: "W",
  KeyX: "X",
  KeyC: "C",
  KeyV: "V",
  KeyB: "B",
  KeyN: "N",
  KeyM: ",",
  Comma: ";",
  Period: ":",
  Slash: "!",
};

/**
 * The .SC2Hotkeys serialization token per code where AZERTY's token diverges from charToken of its
 * character. SC2 binds these OEM/symbol keys by virtual-key, so the file names
 * the key, not the char it emits: the "!" key serializes as the token "OEM8" (no character at all),
 * the "ù" key (Quote) as "Grave" (VK_OEM_3 on French AZERTY). Recovered once from TheCore's real
 * US-Qwerty -> France-Azerty pair (the canonical right hand) and verified to reproduce that file
 * command-for-command. Letters and digits are omitted on purpose: letters match charToken(map[code])
 * (the char path), and a digit token is positional (the number row is virtual-key bound). Both
 * convert (code to token) and parse (token to code) read this. The map is injective, so every key
 * round-trips. The "²" key (Backquote) is VK_OEM_7, which SC2 names "Apostrophe" - witnessed by the
 * left-hand file's many Backquote bindings (Hive/Lair, Land, Lift, ...). The right-hand file's only
 * Backquote binding (ChatAllies) was left unconverted on "Grave", a documented TheCore source quirk;
 * an earlier pass saw only the right hand and mis-recorded Backquote, corrected here (both hands).
 */
export const AZERTY_CODE_TO_TOKEN: Record<string, string> = {
  Backquote: "Apostrophe", // ² key, VK_OEM_7
  Minus: "BracketOpen",
  BracketLeft: "BracketClose",
  BracketRight: "SemiColon",
  Backslash: "BackSlash",
  Quote: "Grave",
  Comma: "Period",
  Period: "Slash",
  Slash: "OEM8",
};
